import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, GlobalStyles, IconButton, Menu, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../../Map/components/ActionMenuItem';
import { appModalCloseButtonSx } from '../../Components/appModalChrome.styles';
import logoTerrioNegro from '../../assets/branding/logo-terrio.png';
import { MARKET_FONT } from './MarketParts';
import { MarketToneProvider } from '../marketTone';
import MarketSummary from './MarketSummary';
import MarketComparablesList from './MarketComparablesList';

export const MARKET_REPORT_PRINT_CLASS = 'market-report-print-root';

const reportMenuPaperProps = {
  ...ACTION_MENU_PAPER_PROPS,
  sx: { ...ACTION_MENU_PAPER_PROPS.sx, width: 'auto', minWidth: '164px', maxWidth: 'min(340px, 92vw)' },
};

/** ⋮ del informe: «Descargar o imprimir». Deshabilitado hasta que hay datos. */
export function MarketReportMenu({ disabled, onPrint }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Tooltip title={<span>Más opciones</span>} placement="bottom" arrow>
        <IconButton
          size="small"
          aria-label="Más opciones del informe"
          aria-haspopup="menu"
          aria-expanded={anchor ? 'true' : undefined}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={appModalCloseButtonSx}
          data-testid="market-report-menu"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={reportMenuPaperProps}
        MenuListProps={{ ...ACTION_MENU_LIST_PROPS, 'aria-label': 'Opciones del informe' }}
      >
        <ActionMenuItem
          icon={<PrintOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Descargar o imprimir"
          disabled={disabled}
          testId="market-report-menu-print"
          onClick={() => { setAnchor(null); onPrint(); }}
        />
      </Menu>
    </>
  );
}

const muted = { fontFamily: MARKET_FONT, fontSize: '13px', color: '#5f6b73', lineHeight: 1.4 };
const head = { fontFamily: MARKET_FONT, fontWeight: 700, color: '#111111', lineHeight: 1.2 };

/**
 * Hoja de «Descargar o imprimir»: el mismo informe (resumen + comparables) en fondo blanco.
 * Sin el mapa. El navegador lo guarda como PDF.
 *
 * @param {{ data: object, block: object, subtitle?: string, filtersLabel?: string }} props
 */
export function MarketReportDocument({ data, block, subtitle = '', filtersLabel = '' }) {
  const comparables = block?.comparables || [];
  const total = block?.nComparables;
  const count = comparables.length
    ? `${comparables.length}${total && total > comparables.length ? ` de ${total}` : ''} · más parecidas primero`
    : '';
  return (
    <MarketToneProvider tone="paper">
      <Box
        component="article"
        data-testid="market-report-print-doc"
        sx={{
          display: 'flex', flexDirection: 'column', gap: 1.75, color: '#1d2327', fontFamily: MARKET_FONT,
          printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact',
        }}
      >
        <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
            <Box component="img" src={logoTerrioNegro} alt="Terrio" sx={{ height: 22, width: 'auto', display: 'block' }} />
            <Typography sx={{ ...muted, fontSize: '12.5px' }}>Comparación de mercado</Typography>
          </Box>
          <Typography component="h1" sx={{ ...head, fontSize: '22px' }}>
            {subtitle || 'Comparación de mercado'}
          </Typography>
          {filtersLabel ? <Typography sx={muted}>{filtersLabel}</Typography> : null}
        </Box>
        <MarketSummary data={data} block={block} />
        {comparables.length ? (
          <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="h2" sx={{ ...head, fontSize: '15px' }}>Comparables</Typography>
            <Typography sx={{ ...muted, fontSize: '12px' }}>{count}</Typography>
            <MarketComparablesList block={block} />
          </Box>
        ) : null}
      </Box>
    </MarketToneProvider>
  );
}

/**
 * @param {object|null} report Datos ya cargados. `null` = todavía no hay informe para imprimir.
 * @returns {{ print: () => void, printNode: React.ReactNode }}
 */
export function useMarketReportPrint(report) {
  const [printing, setPrinting] = useState(false);
  const print = useCallback(() => { if (report) setPrinting(true); }, [report]);

  useEffect(() => {
    if (!printing) return undefined;
    const id = window.requestAnimationFrame(() => {
      try {
        if (typeof window.print === 'function') window.print();
      } finally {
        setPrinting(false);
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [printing]);

  const printNode = printing && report ? (
    <MarketReportPrintPortal report={report} />
  ) : null;
  return { print, printNode };
}

const printStyles = (
  <GlobalStyles
    styles={{
      '@page': { size: 'A4', margin: '14mm' },
      '@media print': {
        [`body > *:not(.${MARKET_REPORT_PRINT_CLASS})`]: { display: 'none !important' },
        [`.${MARKET_REPORT_PRINT_CLASS}`]: { display: 'block !important' },
        body: { background: '#FFFFFF !important' },
      },
    }}
  />
);

function MarketReportPrintPortal({ report }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <Box className={MARKET_REPORT_PRINT_CLASS} data-testid="market-report-print" sx={{ display: 'none', bgcolor: '#FFFFFF', color: '#1d2327' }}>
      {printStyles}
      <MarketReportDocument {...report} />
    </Box>,
    document.body,
  );
}
